import React, { useEffect, useMemo, useState } from 'react';
import { Button, Form, Icon, Modal } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';

export const validateProxySubscriptionUrl = (value, required = true) => {
  const candidate = (value || '').trim();
  if (!candidate) return required ? 'required' : '';
  try {
    const parsed = new URL(candidate);
    if (!['http:', 'https:'].includes(parsed.protocol)) return 'protocol';
    if (parsed.username || parsed.password) return 'credentials';
    return '';
  } catch (error) {
    return 'invalid';
  }
};

const ProxySubscriptionModal = ({
  open,
  subscription,
  loading,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: '', url: '', enabled: true });
  const [touched, setTouched] = useState({ name: false, url: false });

  useEffect(() => {
    setForm({
      name: subscription?.name || '',
      url: '',
      enabled: subscription ? subscription.enabled !== false : true,
    });
    setTouched({ name: false, url: false });
  }, [subscription, open]);

  const urlError = useMemo(
    () => validateProxySubscriptionUrl(form.url, !subscription),
    [form.url, subscription]
  );
  const nameError = !form.name.trim();
  const valid = !nameError && !urlError;

  const change = (event, { name, value, checked }) => {
    setForm((current) => ({
      ...current,
      [name]: name === 'enabled' ? checked : value,
    }));
  };

  const submit = () => {
    setTouched({ name: true, url: true });
    if (!valid) return;
    const payload = { name: form.name.trim(), enabled: form.enabled };
    if (form.url.trim()) payload.url = form.url.trim();
    onSubmit(payload);
  };

  return (
    <Modal
      open={open}
      onClose={loading ? undefined : onClose}
      size='small'
      closeIcon={!loading}
      className='studio-modal proxy-subscription-modal'
      closeOnDimmerClick={!loading}
    >
      <Modal.Header>
        <span className='proxy-modal-icon' aria-hidden='true'>
          <Icon name={subscription ? 'edit outline' : 'plus'} />
        </span>
        <div>
          <span className='studio-section-eyebrow'>PROXY SUBSCRIPTION</span>
          {t(
            subscription
              ? 'setting.proxy_subscription.edit_title'
              : 'setting.proxy_subscription.create_title'
          )}
        </div>
      </Modal.Header>
      <Modal.Content>
        <p className='proxy-modal-lead'>
          {t('setting.proxy_subscription.form_description')}
        </p>
        <Form loading={loading} onSubmit={submit}>
          <Form.Input
            label={t('setting.proxy_subscription.name')}
            name='name'
            value={form.name}
            onChange={change}
            onBlur={() => setTouched((current) => ({ ...current, name: true }))}
            placeholder={t('setting.proxy_subscription.name_placeholder')}
            error={
              touched.name && nameError
                ? {
                    content: t(
                      'setting.proxy_subscription.validation.name_required'
                    ),
                  }
                : null
            }
            autoFocus
          />
          <Form.Input
            label={t('setting.proxy_subscription.url')}
            name='url'
            value={form.url}
            onChange={change}
            onBlur={() => setTouched((current) => ({ ...current, url: true }))}
            type='url'
            placeholder={t(
              subscription
                ? 'setting.proxy_subscription.url_edit_placeholder'
                : 'setting.proxy_subscription.url_placeholder'
            )}
            error={
              touched.url && urlError
                ? {
                    content: t(
                      `setting.proxy_subscription.validation.url_${urlError}`
                    ),
                  }
                : null
            }
            autoComplete='off'
          />
          <div className='proxy-security-note'>
            <Icon name='shield alternate' />
            <span>
              {t(
                subscription
                  ? 'setting.proxy_subscription.url_edit_hint'
                  : 'setting.proxy_subscription.url_security_hint'
              )}
            </span>
          </div>
          <Form.Checkbox
            label={t('setting.proxy_subscription.enabled')}
            name='enabled'
            checked={form.enabled}
            onChange={change}
          />
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose} disabled={loading}>
          {t('setting.proxy_subscription.cancel')}
        </Button>
        <Button primary onClick={submit} loading={loading} disabled={!valid}>
          {t('setting.proxy_subscription.save')}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default ProxySubscriptionModal;
